<template>
  <div>
    <div v-if="props.viewType === DataViewType.Grid">
      <PackageCard
        :created-date="item.created_at"
        :profile-image="profileImage"
        :creator-name="item.content_owner_name"
        :package-name="item.package_name"
        :package-type="DataType.OfferedPackages"
        :cover-image="coverImage"
        :fallback-profile-image="defaultProfileImage"
        :total-videos="item.number_of_videos"
        :offered-package-status="item.status"
        :categories="getPackageCategories()"
        :orientations="item.orientations"
        @offered-package-approval="handleApproval(item.id, true)"
        @offered-package-decline="handleApproval(item.id, false)"
        @click.stop="openEditPackage(item)"
      />
    </div>
    <div v-if="props.viewType === DataViewType.List" class="playlist list">
      <div ref="dataViewRow">
        <div class="content">
          <div>
            <div class="proposal grid" :style="`grid-template-columns:  ${columnsGridTemplate(props.columns)};`" @click="openEditPackage(item)">
              <div class="package-name" :class="shouldHideOnBreakpoint(props.columns, 'package-name')">
                <IconWrapper class="checkmark" name="offeredPackageIcon" />
                <NewTooltip :placement="'top'" :content="item.package_name" auto-ellipsis />
              </div>
              <div class="container" :class="shouldHideOnBreakpoint(props.columns, 'content-creator-name')">
                <img class="profile-pic" :src="profileImage" alt="">
                <div class="name-container">
                  <div class="name">
                    <NewTooltip :placement="'top'" :content="item.content_owner_name" auto-ellipsis />
                  </div>
                </div>
              </div>
              <div :class="shouldHideOnBreakpoint(props.columns, 'status')">
                <Label
                  :text="item.status === offeredPackageStatus.Active ? $t('content-package-accepted') : $t('pending')"
                  :color="item.status === offeredPackageStatus.Active ? LabelColors.Green : LabelColors.GreyWhite"
                />
              </div>
              <div class="sent-at" :class="shouldHideOnBreakpoint(props.columns, 'sent-at')">{{ getUTCDateString(item.created_at) }}</div>
              <div class="accept-action" :class="shouldHideOnBreakpoint(props.columns, 'accept-action')">
                <Button v-if="item.status == offeredPackageStatus.Pending" :variant="ButtonVariant.SECONDARY" :size="ButtonSize.SMALL" @click="handleAccept">
                  {{ $t('accept') }}
                </Button>
              </div>
              <div class="actions" :class="shouldHideOnBreakpoint(props.columns, 'actions')">
                <ActionButtons
                  v-if="item.status == offeredPackageStatus.Pending" :actions="actions" :view-type="DataViewType.List" :is-visible="isRowHovered"
                  @click.stop="handleDecline"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementHover } from '@vueuse/core'
import { CRUD, Category, DataActions, DataType, DataViewType, ListColumn } from '@/interfaces'
import { useRouter } from 'vue-router'
import { getUTCDateString, openModalCustomConfirmation } from '@/services/utils'
import { NewTooltip, IconWrapper, PackageCard, Button, Label } from '@/components/common'
import { columnsGridTemplate, shouldHideOnBreakpoint } from '@/services/layoutUtils'
import { onMounted, ref, onBeforeMount, inject, computed } from 'vue'
import { Proposal, offeredPackageStatus } from '@/services/content/package/types'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import { ModalType } from '@/components/modals/modals-util'
import { apiService } from '@/services'
import { OFFERED_PACKAGES_ACTIONS_INJECTION_KEY } from '@/constants'
import { ButtonSize, ButtonVariant } from '@/styles/types'
import { LabelColors } from '@/components/common/label/utils'
import { useDefaultChannelProfileImages } from '@/composables/defaultChannelProfileImages'
import { getCoverImage } from '../packageCoversUtils'

const router = useRouter()
const props = defineProps<{
  item: Proposal
  viewType: DataViewType
  selected?: boolean
  columns: ListColumn[]
}>()
const viewportWidth = ref(window.innerWidth)
const dataViewRow = ref()
const isRowHovered = useElementHover(dataViewRow)
const modalType = ModalType.OFFERED_PACKAGES_PACKAGE_MODAL
const dataActions = inject<DataActions>(OFFERED_PACKAGES_ACTIONS_INJECTION_KEY)
const { getDefaultProfileImage } = useDefaultChannelProfileImages()
const coverImage = getCoverImage(props.item?.cover_image)
const defaultProfileImage = computed(() => getDefaultProfileImage(props.item.content_owner_org_id))
const profileImage = computed(
  () =>
    props.item?.icon_image ||
    props.item?.content_owner_profile_image ||
    defaultProfileImage.value
)

onMounted(() => {
  window.addEventListener('resize', updateViewportWidth)
})

onBeforeMount(() => {
  window.removeEventListener('resize', updateViewportWidth)
})

const updateViewportWidth = () => {
  viewportWidth.value = window.innerWidth
}
const actions = [
  {
    label: 'delete',
    handler: handleDecline,
    translationKey: 'content-package-decline',
    isDisabled: props.item.status === offeredPackageStatus.Active
  }
]

function getPackageCategories(): string[] {
  return props.item?.categories?.map((category: Category) => String(category.id)) ?? []
}

function handleAccept() {
  handleApproval(props.item.id, true)
}
function handleDecline() {
  handleApproval(props.item.id, false)
}

function openEditPackage(item: Proposal) {
  router.push({
    path: `/offered-packages/edit/${item.id}`,
    name: 'ext-edit-package',
    params: {
      id: item.id
    }
  })
}

function handleApproval(id: string, isApproved: boolean) {
  openModalCustomConfirmation(
    { action: isApproved ? CRUD.ConfirmAccept : CRUD.ConfirmDecline, type: DataType.OfferedPackages, id: id, value: isApproved, onConfirm: isApproved ? acceptPackage : declinePackage },
    modalType
  )
}

async function postApproval(id: string, isApproved: boolean): Promise<void> {
  try {
    await apiService.contentPackage.postPublisherProposal(id, isApproved)
    if (dataActions?.refreshData) dataActions.refreshData()
  } catch (error) {
    console.log(error)
  }
}

const declinePackage = async (params) => {
  await postApproval(params.id, false)
}

const acceptPackage = async (params) => {
  await postApproval(params.id, true)
}

</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.icons-padding {
  padding: 5px;
}

.offered-buttons {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
}

.content {
  height: fit-content;
}

.loading-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 60vh;
}

.profile-pic {
  width: 145px;
  height: 145px;
  border-radius: 100%;
  border: 6px solid #dadbe8;
  box-shadow:
    rgba(50, 50, 93, 0.25) 0px 6px 12px -2px,
    rgba(0, 0, 0, 0.3) 0px 3px 7px -3px;
  display: flex;
  justify-content: center;
  align-content: center;
}

.grid {
  display: grid;
  grid-template-columns: 18% 21% 18% 15% auto;
  align-items: center;

  > div {
    padding: 1rem;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  img:hover {
    filter: $truvid-filter-effect;
  }
}

.proposal {
  height: 80px;
  align-items: center;

  &:hover {
    background: #f3f4fa;
  }

  .date {
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0.1px;
    color: $bodyText;
    opacity: 0.8;
  }

  .container {
    display: flex;
    align-items: center;

    img {
      height: 44px;
      width: 44px;
      border: 1px solid rgba(218, 219, 232, 1);
      border-radius: 100%;
      padding: 1px;
    }
  }

  .name-container {
    display: flex;
    flex-direction: column;
    margin-left: 20px;
  }

  .name {
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0.1px;
    color: $bodyText;
  }

  .package-name {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: 0.625rem;
    overflow: hidden;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0.1px;
    color: $bodyText;
  }
}
</style>
